/**
 * Lead-capture modal — name + email + organisation + consent.
 *
 * Submits to ``POST /api/brochure/leads`` so the existing CRM-side
 * lead pipeline keeps receiving qualified contacts. No PDF is
 * delivered — the public surface intentionally hands out no
 * artefacts. Success state simply confirms receipt and closes.
 *
 * Form contract:
 *   • Name 2–80 chars
 *   • Email RFC-5322 — validated client-side via the HTML5
 *     ``type="email"`` and a regex backstop, then again
 *     server-side via Pydantic ``EmailStr``.
 *   • Organisation 2–120 chars
 *   • Consent checkbox MUST be ticked to submit (button stays
 *     disabled until then)
 *   • Honeypot ``company_phone_alt`` — DOM-present but visually
 *     hidden via inline style; bots tend to fill every input
 *     they see, server-side rejection on non-empty value
 *
 * No anti-abuse beyond the honeypot — PM-confirmed (no CAPTCHA,
 * no IP rate-limit).
 */
import { useEffect, useState } from "react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const EMAIL_RX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const PUBLIC_AXIOS_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function postJson(path, body) {
    const res = await fetch(`${PUBLIC_AXIOS_BASE}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (res.status === 204) return { _silent: true };
    let payload = null;
    try {
        payload = await res.json();
    } catch (_e) {
        payload = null;
    }
    if (!res.ok) {
        const err = new Error(
            (payload && payload.detail) || `Request failed (${res.status})`,
        );
        err.status = res.status;
        err.payload = payload;
        throw err;
    }
    return payload;
}

export default function BrochureRequestModal({ open, onOpenChange }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [organisation, setOrganisation] = useState("");
    const [consent, setConsent] = useState(false);
    // Honeypot — never displayed, never user-touched. Bots that
    // auto-fill every input they see will populate this and trigger
    // a silent server reject.
    const [companyPhoneAlt, setCompanyPhoneAlt] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    // Reset form when the modal closes so a re-open is fresh.
    useEffect(() => {
        if (!open) {
            setName("");
            setEmail("");
            setOrganisation("");
            setConsent(false);
            setCompanyPhoneAlt("");
            setError("");
            setSuccess(false);
            setSubmitting(false);
        }
    }, [open]);

    const valid =
        name.trim().length >= 2 &&
        name.trim().length <= 80 &&
        EMAIL_RX.test(email.trim()) &&
        organisation.trim().length >= 2 &&
        organisation.trim().length <= 120 &&
        consent;

    async function onSubmit(e) {
        if (e?.preventDefault) e.preventDefault();
        if (!valid || submitting) return;
        setSubmitting(true);
        setError("");
        try {
            await postJson("/api/brochure/leads", {
                name: name.trim(),
                email: email.trim(),
                organisation: organisation.trim(),
                consent,
                company_phone_alt: companyPhoneAlt,
            });
            // Whether the server returned a 204 (honeypot silent
            // reject) or a 200/201 with a payload, the public
            // surface confirmation is identical — we do not hand
            // out any artefact, so there is nothing further to do
            // beyond acknowledging receipt.
            setSuccess(true);
        } catch (err) {
            setError(
                err?.payload?.detail ||
                    err?.message ||
                    "Something went wrong. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md"
                data-testid="brochure-request-modal"
            >
                <DialogHeader>
                    <DialogTitle>Request an Assessment</DialogTitle>
                    <DialogDescription>
                        Tell us a little about your team and we&apos;ll be in touch
                        to scope a structured simulation-based assessment.
                    </DialogDescription>
                </DialogHeader>

                {success ? (
                    <div
                        className="space-y-4 py-2"
                        data-testid="brochure-request-success"
                    >
                        <p
                            className="text-base font-semibold"
                            style={{ color: "#0b1f3a" }}
                        >
                            Thanks — we&apos;ll be in touch.
                        </p>
                        <p
                            className="text-sm"
                            style={{ color: "#475569" }}
                        >
                            Your details have reached the UAVSimNet team. A
                            human will reply from a uavsimnet.com address to
                            scope next steps.
                        </p>
                        <DialogFooter>
                            <Button
                                onClick={() => onOpenChange(false)}
                                data-testid="brochure-request-success-close"
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        className="space-y-3"
                        data-testid="brochure-request-form"
                    >
                        <div className="space-y-1">
                            <Label htmlFor="brochure-name">Name</Label>
                            <Input
                                id="brochure-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                minLength={2}
                                maxLength={80}
                                data-testid="brochure-name-input"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="brochure-email">Email</Label>
                            <Input
                                id="brochure-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="brochure-email-input"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="brochure-organisation">
                                Organisation
                            </Label>
                            <Input
                                id="brochure-organisation"
                                value={organisation}
                                onChange={(e) =>
                                    setOrganisation(e.target.value)
                                }
                                required
                                minLength={2}
                                maxLength={120}
                                data-testid="brochure-organisation-input"
                            />
                        </div>

                        {/* Honeypot — DOM-present, visually hidden, never
                            tab-reachable. Bots that auto-fill all inputs
                            will populate this and the server returns a
                            silent 204. */}
                        <input
                            type="text"
                            name="company_phone_alt"
                            value={companyPhoneAlt}
                            onChange={(e) =>
                                setCompanyPhoneAlt(e.target.value)
                            }
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                left: "-9999px",
                                width: "1px",
                                height: "1px",
                                opacity: 0,
                                pointerEvents: "none",
                            }}
                            data-testid="brochure-honeypot"
                        />

                        <div className="flex items-start gap-2 pt-1">
                            <Checkbox
                                id="brochure-consent"
                                checked={consent}
                                onCheckedChange={(v) => setConsent(Boolean(v))}
                                data-testid="brochure-consent-checkbox"
                            />
                            <Label
                                htmlFor="brochure-consent"
                                className="text-xs leading-snug font-normal"
                            >
                                I consent to UAVSimNet contacting me regarding
                                this enquiry and storing my details in line
                                with the privacy policy.
                            </Label>
                        </div>

                        {error ? (
                            <p
                                className="text-sm text-red-700"
                                data-testid="brochure-request-error"
                            >
                                {error}
                            </p>
                        ) : null}

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={submitting}
                                data-testid="brochure-request-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!valid || submitting}
                                data-testid="brochure-request-submit"
                            >
                                {submitting ? "Sending..." : "Request Assessment"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
